import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Highlighter from 'react-highlight-words';

import { connect } from 'react-redux';
import * as Yup from 'yup';

import {
	PageHeader,
	Form,
	Select,
	Button,
	Input,
	Tabs,
	Icon,
	Typography,
	Divider,
	Modal,
	Checkbox,
	Progress,
	Card,
	message,
	Avatar,
	Tag,
} from 'antd';

import Meta from '../../../../components/Meta';
import Fallback from '../../../../components/Fallback';
import UploadZone from '../../../../components/UploadZone';
import { Container, ButtonsContainer, highlightStyle } from './styles';

import GenresAPI from '../../../../services/sdks/genres';
import ProgramsAPI from '../../../../services/sdks/programs';
import ElementsAPI from '../../../../services/sdks/elements';
import UsersAPI from '../../../../services/sdks/user';
import CastersAPI from '../../../../services/sdks/caster';
import { CasterOption } from '../ManagePrograms/styles';
import { resolveFileSrc } from '../../../../helpers/fileSrcResolver';
import env from '../../../../config/env';
import AiModelsAPI from '../../../../services/sdks/ai-models';

const layout = { labelCol: { span: 8 }, wrapperCol: { span: 16 } };
const breadcrumb = {
	routes: [
		{ breadcrumbName: 'Admin' },
		{ breadcrumbName: 'Conteúdos' },
		{ breadcrumbName: 'Criar Programa' },
	],
	style: { marginBottom: 24 },
};

const RuleInput = ({ ruleKey, label, accountInfos, handleChangeAccountInfo, placeholderText }) => {
	const [inputValue, setInputValue] = React.useState('');

	const handleAddRule = (e) => {
		e.preventDefault();
		const value = inputValue.trim();
		if (!value) return;

		const updated = {
			...accountInfos.rules,
			[ruleKey]: [...(accountInfos.rules?.[ruleKey] || []), value],
		};

		handleChangeAccountInfo('rules', updated);

		setInputValue('');
	};

	const handleRemoveRule = (ruleToRemove) => {
		const updated = {
			...accountInfos.rules,
			[ruleKey]: accountInfos.rules[ruleKey].filter((rule) => rule !== ruleToRemove),
		};

		handleChangeAccountInfo('rules', updated);
	};

	return (
		<Form.Item
			label={label}
			labelCol={{ span: 24 }}
			wrapperCol={{ span: 24 }}
			style={{ marginBottom: 0 }}>
			<>
				<Input.TextArea
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					placeholder={placeholderText}
					autoSize={{ minRows: 1, maxRows: 3 }}
					onPressEnter={handleAddRule}
				/>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 8,
						marginTop: 8,
					}}>
					{(accountInfos.rules?.[ruleKey] || []).map((rule, index) => (
						<Tag
							key={`${ruleKey}-${rule}-${index}`}
							closable
							onClose={(e) => {
								e.preventDefault();
								handleRemoveRule(rule);
							}}
							style={{
								whiteSpace: 'normal',
								lineHeight: 1.5,
								padding: '8px 12px',
								fontSize: 14,
								background: '#fafafa',
								border: '1px solid #d9d9d9',
							}}>
							{rule}
						</Tag>
					))}
				</div>
			</>
		</Form.Item>
	);
};

const CreateProgram = () => {
	const [fallback, setFallback] = useState({ initialData: true });
	const [search, setSearch] = useState(['']);
	const [casters, setCasters] = useState(['']);
	const [users, setUsers] = useState([]);
	const [genres, setGenres] = useState([]);
	const [infos, setInfos] = useState({
		name: '',
		description: '',
		filenameTemplate: '',
		isEditable: false,
		userId: undefined,
		genreId: undefined,
		caster: undefined,
		audience: '',
		theme: '',
	});
	const [aiModels, setAiModels] = useState([]);
	const [selectedAiModels, setSelectedAiModels] = useState([]);

	const [vignettes, setVignettes] = useState({
		programIntro: [],
		programOutro: [],
		blockIntro: [],
		blockOutro: [],
		break: [],
	});

	const [progress, setProgress] = useState({
		programIntro: null,
		programOutro: null,
		blockIntro: null,
		blockOutro: null,
		break: null,
	});

	const handleChangeInfo = useCallback((key, value) => {
		setInfos((prev) => ({ ...prev, [key]: value }));
	}, []);

	const handlePickVignettes = useCallback((key, value) => {
		setVignettes((prev) => ({ ...prev, [key]: [...value] }));
	}, []);

	const handleRemoveVignettes = useCallback(
		(key, index) => {
			const updatedVignettes = vignettes[key].filter((_, i) => {
				return i !== index;
			});

			setVignettes((prev) => ({ ...prev, [key]: updatedVignettes }));
		},
		[vignettes]
	);

	const uploadVignettes = useCallback(
		async (programId) => {
			try {
				setFallback((prev) => ({ ...prev, uploading: true }));

				const promises = [];

				if (vignettes?.programIntro.length) {
					const payload = new FormData();

					payload.append('type', 'VIGNETTE-PROGRAM-INTRO');
					payload.append('category', 'VIGNETTE');
					payload.append('programId', programId);
					vignettes.programIntro.forEach((file, i) => payload.append(`file-${i}`, file));

					const promise = ElementsAPI.store({
						payload,
						onUploadProgress: ({ total, loaded }) => {
							const percentage = Math.floor((loaded * 100) / total);

							setProgress((prev) => ({ ...prev, programIntro: percentage }));
						},
					});

					promises.push(promise);
				}

				if (vignettes?.programOutro.length) {
					const payload = new FormData();

					payload.append('type', 'VIGNETTE-PROGRAM-OUTRO');
					payload.append('category', 'VIGNETTE');
					payload.append('programId', programId);
					payload.append('userId', infos?.userId);
					vignettes.programOutro.forEach((file, i) => payload.append(`file-${i}`, file));

					const promise = ElementsAPI.store({
						payload,
						onUploadProgress: ({ total, loaded }) => {
							const percentage = Math.floor((loaded * 100) / total);

							setProgress((prev) => ({ ...prev, programOutro: percentage }));
						},
					});

					promises.push(promise);
				}

				if (vignettes?.blockIntro.length) {
					const payload = new FormData();

					payload.append('type', 'VIGNETTE-BLOCK-INTRO');
					payload.append('category', 'VIGNETTE');
					payload.append('programId', programId);
					payload.append('userId', infos?.userId);
					vignettes.blockIntro.forEach((file, i) => payload.append(`file-${i}`, file));

					const promise = ElementsAPI.store({
						payload,
						onUploadProgress: ({ total, loaded }) => {
							const percentage = Math.floor((loaded * 100) / total);

							setProgress((prev) => ({ ...prev, blockIntro: percentage }));
						},
					});

					promises.push(promise);
				}

				if (vignettes?.blockOutro.length) {
					const payload = new FormData();

					payload.append('type', 'VIGNETTE-BLOCK-OUTRO');
					payload.append('category', 'VIGNETTE');
					payload.append('programId', programId);
					payload.append('userId', infos?.userId);
					vignettes.blockOutro.forEach((file, i) => payload.append(`file-${i}`, file));

					const promise = ElementsAPI.store({
						payload,
						onUploadProgress: ({ total, loaded }) => {
							const percentage = Math.floor((loaded * 100) / total);

							setProgress((prev) => ({ ...prev, blockOutro: percentage }));
						},
					});

					promises.push(promise);
				}

				if (vignettes?.break.length) {
					const payload = new FormData();

					payload.append('type', 'VIGNETTE-BREAK');
					payload.append('category', 'VIGNETTE');
					payload.append('programId', programId);
					payload.append('userId', infos?.userId);
					vignettes.break.forEach((file, i) => payload.append(`file-${i}`, file));

					const promise = ElementsAPI.store({
						payload,
						onUploadProgress: ({ total, loaded }) => {
							const percentage = Math.floor((loaded * 100) / total);

							setProgress((prev) => ({ ...prev, break: percentage }));
						},
					});

					promises.push(promise);
				}

				await Promise.allSettled(promises);

				setVignettes({
					programIntro: [],
					programOutro: [],
					blockIntro: [],
					blockOutro: [],
					break: [],
				});
				setProgress({
					programIntro: null,
					programOutro: null,
					blockIntro: null,
					blockOutro: null,
					break: null,
				});
				setInfos({
					name: '',
					description: '',
					filenameTemplate: '',
					isEditable: false,
					userId: undefined,
					genreId: undefined,
				});

				setSearch(['']);
				setFallback({ initialData: false });

				message.success('Programa criado com sucesso!');
			} catch (error) {
				console.error(error);
				message.error('Houve um erro, tente novamente');
			}
		},
		[vignettes, infos]
	);

	const handleCreateProgram = useCallback(async () => {
		try {
			const payload = infos;
			const validationSchema = Yup.object().shape({
				userId: Yup.string().required('Informe o usuário (dono do programa)'),
				filenameTemplate: Yup.string().required('Informe o nome padrão dos arquivos'),
				description: Yup.string().required('Informe a descrição do programa'),
				name: Yup.string().required('Informe o nome do programa'),
				audience: Yup.string().required('Informe o público alvo'),
				theme: Yup.string().required('Informe o tema do programa'),
				dayPeriod: Yup.string()
					.nullable()
					.notRequired()
					.oneOf(
						[
							'MORNING',
							'LATE_MORNING',
							'AFTERNOON',
							'LATE_AFTERNOON',
							'NIGHT',
							'LATE_NIGHT',
							'DAWN',
							null,
						],
						'dayPeriod inválido'
					),
			});

			await validationSchema.validate(payload);

			setFallback((prev) => ({ ...prev, creatingProgram: true }));

			const {
				data: { program },
			} = await ProgramsAPI.store({ ...payload, aiModelIds: selectedAiModels });

			setFallback((prev) => ({ ...prev, creatingProgram: false }));

			await uploadVignettes(program?._id);
		} catch (error) {
			console.error(error);
			console.error({ ...error });

			setFallback((prev) => ({ ...prev, creatingProgram: false }));

			if (error instanceof Yup.ValidationError) {
				return message.error(error.message);
			}

			if (error?.response?.data?.message) {
				message.error(error?.response?.data?.message);
			} else {
				message.error('Houve um erro, tente novamente');
			}
		}
	}, [infos, selectedAiModels, uploadVignettes]);

	useEffect(() => {
		async function getUsers() {
			const res = await UsersAPI.index('active=true&pageNumber=0&pageSize=99999');
			return res.data.users;
		}

		async function getCasters() {
			const res = await CastersAPI.index(`active=true&page=0&limit=9999`);
			return res.data.casters;
		}

		async function getGenres() {
			const res = await GenresAPI.index();
			return res.data.genres;
		}

		const fetchInitialData = async () => {
			try {
				const [users, casters, genres] = await Promise.all([getUsers(), getCasters(), getGenres()]);

				setUsers(users);
				setCasters(casters);
				setGenres(genres);
				setFallback((prev) => ({ ...prev, initialData: false }));
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar os gêneros, tente novamente');
			}
		};

		fetchInitialData();
	}, []);

	const getAiModels = async (userId) => {
		const { data } = await AiModelsAPI.listAiModels({ userId, pageSize: 9999 });
		const { total, aiModels } = data;
		if (total) {
			setSelectedAiModels([]);
			setAiModels(
				aiModels.map((aiModel) => {
					return { value: aiModel._id, label: aiModel.title };
				})
			);
		} else {
			setAiModels([]);
			setSelectedAiModels([]);
		}
	};

	useEffect(() => {
		if (infos.userId) {
			getAiModels(infos.userId);
		}
	}, [infos.userId]);

	const userCasters = useMemo(() => {
		if (!infos?.userId) {
			return casters.filter((c) => c.userId === env.talkId);
		}

		return casters.filter((c) => {
			return c.userId === env.talkId || c.userId === infos?.userId;
		});
	}, [casters, infos?.userId]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor, aguarde...' />;
	}

	return (
		<>
			<Meta title='Criar programa' />

			<PageHeader title='Criar Programa' breadcrumb={breadcrumb} />

			<Container>
				<Card>
					<Tabs tabPosition='left'>
						<Tabs.TabPane
							key='infos'
							tab={
								<>
									<Icon type='file-text' /> Informações
								</>
							}>
							<Form {...layout} onSubmit={handleCreateProgram}>
								<Form.Item label='Usuário (dono do programa)' required>
									<Select
										showSearch
										optionFilterProp='children'
										placeholder='Selecione o usuário'
										size='large'
										value={infos?.userId}
										onSearch={(value) => setSearch([value])}
										onChange={(value) => {
											handleChangeInfo('userId', value);
										}}
										filterOption={(input, { props: { _search } }) => {
											const regex = new RegExp(input, 'i');

											return _search.match(regex);
										}}>
										{users.map((user) => {
											const { radioName, city, state, email } = user;

											return (
												<Select.Option
													_search={`${radioName}${city}${state}${email}`}
													key={user?._id}
													value={user?._id}>
													<Highlighter
														highlightStyle={highlightStyle}
														searchWords={search}
														textToHighlight={`${radioName} - ${city}/${state} (${email})`}
													/>
												</Select.Option>
											);
										})}
									</Select>
								</Form.Item>

								<Form.Item
									label='Modelos de IA'
									help={
										!aiModels.length && infos.userId ? 'Esta rádio não possui modelos de IA' : ''
									}>
									<div
										style={{
											display: 'flex',
											justifyContent: 'flex-start',
											gap: 8,
											marginBottom: 8,
										}}></div>
									<Select
										mode='multiple'
										allowClear
										showSearch
										style={{ width: '100%' }}
										placeholder='Digite para buscar ou selecione'
										onChange={setSelectedAiModels}
										value={selectedAiModels}
										size='large'
										filterOption={(input, option) =>
											option?.props?.children?.toLowerCase()?.includes(input.toLowerCase()) ?? false
										}>
										{aiModels.map((aiModel) => (
											<Select.Option key={aiModel.value} value={aiModel.value}>
												{aiModel.label}
											</Select.Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item label='Locutor (Opcional)'>
									<Select
										showSearch
										size='large'
										optionFilterProp='children'
										placeholder='Selecione o locutor'
										value={infos?.caster}
										onSearch={(value) => setSearch([value])}
										onChange={(value) => {
											handleChangeInfo('caster', value);
										}}
										filterOption={(input, { props: { _search } }) => {
											const regex = new RegExp(input, 'i');
											return _search.match(regex);
										}}>
										{userCasters.map((caster) => {
											const { name, surname } = caster;

											return (
												<Select.Option
													_search={`${name}${surname}`}
													key={caster._id}
													value={caster._id}>
													<CasterOption>
														<div>
															<Avatar
																style={{ background: 'var(--primary)' }}
																src={resolveFileSrc({
																	fileName: caster?.profilePic,
																	folder: ['profile_pics'],
																	bucket: env.miscS3Bucket,
																})}>
																{caster?.name[0] || '-'} {caster?.surname[0] || '-'}
															</Avatar>
															<Typography.Text className=''>
																{caster?.name} {caster?.surname}
															</Typography.Text>
														</div>
														<small>
															{caster.userId === env.talkId ? 'LOCUTOR TALK' : 'LOCUTOR DA RÁDIO'}
														</small>
													</CasterOption>
												</Select.Option>
											);
										})}
									</Select>
								</Form.Item>

								<Form.Item label='Gênero do programa'>
									<Select
										showSearch
										size='large'
										value={infos?.genreId}
										onChange={(value) => handleChangeInfo('genreId', value)}
										optionFilterProp='children'
										placeholder='Selecione o gênero'
										filterOption={(input, { props: { children } }) => {
											return children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
										}}>
										{genres.map((genre) => (
											<Select.Option key={genre._id} value={genre._id}>
												{genre?.name}
											</Select.Option>
										))}
									</Select>
								</Form.Item>

								<Form.Item label='Nome' required>
									<Input
										size='large'
										placeholder='Informe o nome do programa'
										value={infos?.name}
										onChange={({ target: { value } }) => {
											handleChangeInfo('name', value);
										}}
									/>
								</Form.Item>

								<Form.Item label='Nome padrão dos arquivos' required>
									<Input
										size='large'
										placeholder='Informe o nome padrão dos arquivos'
										value={infos?.filenameTemplate}
										onChange={({ target: { value } }) => {
											handleChangeInfo('filenameTemplate', value);
										}}
									/>
								</Form.Item>

								<Form.Item size={2} label='Público Alvo' required>
									<Input
										name='audience'
										value={infos.audience}
										size='large'
										placeholder='Ex: Jovens adultos entre 18 e 35 anos'
										onChange={({ target: { value } }) => {
											handleChangeInfo('audience', value);
										}}
									/>
								</Form.Item>
								<Form.Item size={2} label='Tema do Programa' required>
									<Input
										name='theme'
										value={infos.theme}
										size='large'
										placeholder='Ex: Entretenimento e música pop'
										onChange={({ target: { value } }) => {
											handleChangeInfo('theme', value);
										}}
									/>
								</Form.Item>

								<Form.Item label='Período do Dia'>
									<Select
										allowClear
										size='large'
										placeholder='Selecione o período do dia do programa (opcional)'
										value={infos?.dayPeriod}
										onChange={(value) => handleChangeInfo('dayPeriod', value)}>
										<Select.Option value='MORNING'>Manhã</Select.Option>
										<Select.Option value='LATE_MORNING'>Final da Manhã</Select.Option>
										<Select.Option value='AFTERNOON'>Tarde</Select.Option>
										<Select.Option value='LATE_AFTERNOON'>Final da Tarde</Select.Option>
										<Select.Option value='NIGHT'>Noite</Select.Option>
										<Select.Option value='LATE_NIGHT'>Final da Noite</Select.Option>
										<Select.Option value='DAWN'>Madrugada</Select.Option>
									</Select>
								</Form.Item>

								<Form.Item label='Descrição' style={{ marginTop: 16 }} required>
									<Input.TextArea
										placeholder='Descreva o programa'
										value={infos?.description}
										onChange={({ target: { value } }) => {
											handleChangeInfo('description', value);
										}}
									/>
								</Form.Item>

								<Form.Item label='Personalidade' style={{ marginTop: 16 }} optional>
									<Input.TextArea
										placeholder='Descreva a Personalidade'
										value={infos?.personality}
										onChange={({ target: { value } }) => {
											handleChangeInfo('personality', value);
										}}
									/>
								</Form.Item>

								<Form.Item label='Sotaque' style={{ marginTop: 16 }} optional>
									<Input.TextArea
										placeholder='Descreva o Sotaque'
										value={infos?.accent}
										onChange={({ target: { value } }) => {
											handleChangeInfo('accent', value);
										}}
									/>
								</Form.Item>

								<Form.Item label='Características Regionais' style={{ marginTop: 16 }} optional>
									<Input.TextArea
										placeholder='Descreva as Características Regionais'
										value={infos?.regionalCharacteristics}
										onChange={({ target: { value } }) => {
											handleChangeInfo('regionalCharacteristics', value);
										}}
									/>
								</Form.Item>

								<Form.Item optional label='Regras' style={{ marginBottom: 0 }} size={6}>
									<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
										{[
											{
												key: 'mandatory',
												label: 'Obrigatórias',
												placeholderText: 'Digite uma regra obrigatória e pressione Enter',
											},
											{
												key: 'forbidden',
												label: 'Proibidas',
												placeholderText: 'Digite uma regra proibida e pressione Enter',
											},
											{
												key: 'recommended',
												label: 'Recomendadas',
												placeholderText: 'Digite uma regra recomendada e pressione Enter',
											},
										].map(({ key, label, placeholderText }) => (
											<RuleInput
												key={key}
												ruleKey={key}
												label={label}
												placeholderText={placeholderText}
												accountInfos={infos}
												handleChangeAccountInfo={handleChangeInfo}
											/>
										))}
									</div>
								</Form.Item>

								<Form.Item label='' wrapperCol={{ offset: 6 }}>
									<Checkbox
										checked={infos?.isEditable}
										onChange={({ target: { checked } }) => {
											handleChangeInfo('isEditable', checked);
										}}>
										Usuário poderá editar o programa
									</Checkbox>
								</Form.Item>
							</Form>
						</Tabs.TabPane>

						<Tabs.TabPane
							key='vignettes'
							tab={
								<>
									<Icon type='unordered-list' /> Adicionar vinhetas
								</>
							}>
							<Typography.Paragraph style={{ fontStyle: 'italic' }}>
								<span style={{ color: 'red' }}>*</span> Essa etapa é opcional, as vinhetas podem ser
								adicionadas depois pelo acesso do cliente.
							</Typography.Paragraph>

							<Divider />

							<Tabs size='small' tabPosition='left'>
								<Tabs.TabPane
									key='programIntro'
									tab={`(${vignettes?.programIntro.length}) Vinhetas de introdução de programa`}>
									<UploadZone
										id='program-intro'
										label='Clique para selecionar os arquivos'
										secondaryLabel={
											<p>
												Selecione arquivos com a extensão <strong>MP3</strong> ou{' '}
												<strong>WAV</strong>
											</p>
										}
										icon='cloud-upload'
										uploadIcon='customer-service'
										inputProps={{ accept: 'audio/mp3, audio/wav', multiple: true }}
										uploads={vignettes?.programIntro}
										onChange={({ target: { files } }) => {
											return handlePickVignettes('programIntro', files);
										}}
										onRemoveItem={(index) => {
											return handleRemoveVignettes('programIntro', index);
										}}
									/>
								</Tabs.TabPane>

								<Tabs.TabPane
									key='programOutro'
									tab={`(${vignettes?.programOutro.length}) Vinhetas de encerramento de programa`}>
									<UploadZone
										id='program-outro'
										label='Clique para selecionar os arquivos'
										secondaryLabel={
											<p>
												Selecione arquivos com a extensão <strong>MP3</strong> ou{' '}
												<strong>WAV</strong>
											</p>
										}
										icon='cloud-upload'
										uploadIcon='customer-service'
										inputProps={{ accept: 'audio/mp3, audio/wav', multiple: true }}
										uploads={vignettes?.programOutro}
										onChange={({ target: { files } }) => {
											return handlePickVignettes('programOutro', files);
										}}
										onRemoveItem={(index) => {
											return handleRemoveVignettes('programOutro', index);
										}}
									/>
								</Tabs.TabPane>

								<Tabs.TabPane
									key='blockIntro'
									tab={`(${vignettes?.blockIntro.length}) Vinhetas de introdução de bloco`}>
									<UploadZone
										id='block-intro'
										label='Clique para selecionar os arquivos'
										secondaryLabel={
											<p>
												Selecione arquivos com a extensão <strong>MP3</strong> ou{' '}
												<strong>WAV</strong>
											</p>
										}
										icon='cloud-upload'
										uploadIcon='customer-service'
										inputProps={{ accept: 'audio/mp3, audio/wav', multiple: true }}
										uploads={vignettes?.blockIntro}
										onChange={({ target: { files } }) => {
											return handlePickVignettes('blockIntro', files);
										}}
										onRemoveItem={(index) => {
											return handleRemoveVignettes('blockIntro', index);
										}}
									/>
								</Tabs.TabPane>

								<Tabs.TabPane
									key='blockOutro'
									tab={`(${vignettes?.blockOutro.length}) Vinhetas de encerramento de bloco`}>
									<UploadZone
										id='block-outro'
										label='Clique para selecionar os arquivos'
										secondaryLabel={
											<p>
												Selecione arquivos com a extensão <strong>MP3</strong> ou{' '}
												<strong>WAV</strong>
											</p>
										}
										icon='cloud-upload'
										uploadIcon='customer-service'
										inputProps={{ accept: 'audio/mp3, audio/wav', multiple: true }}
										uploads={vignettes?.blockOutro}
										onChange={({ target: { files } }) => {
											return handlePickVignettes('blockOutro', files);
										}}
										onRemoveItem={(index) => {
											return handleRemoveVignettes('blockOutro', index);
										}}
									/>
								</Tabs.TabPane>

								<Tabs.TabPane key='break' tab={`(${vignettes?.break.length}) Vinhetas de passagem`}>
									<UploadZone
										id='break'
										label='Clique para selecionar os arquivos'
										secondaryLabel={
											<p>
												Selecione arquivos com a extensão <strong>MP3</strong> ou{' '}
												<strong>WAV</strong>
											</p>
										}
										icon='cloud-upload'
										uploadIcon='customer-service'
										inputProps={{ accept: 'audio/mp3, audio/wav', multiple: true }}
										uploads={vignettes?.break}
										onChange={({ target: { files } }) => {
											return handlePickVignettes('break', files);
										}}
										onRemoveItem={(index) => {
											return handleRemoveVignettes('break', index);
										}}
									/>
								</Tabs.TabPane>
							</Tabs>
						</Tabs.TabPane>
					</Tabs>

					<Divider />

					<ButtonsContainer>
						<Button
							loading={fallback?.creatingProgram}
							size='large'
							type='primary'
							onClick={handleCreateProgram}
							htmlType='button'>
							Finalizar criação do programa <Icon type='save' />
						</Button>
					</ButtonsContainer>
				</Card>
			</Container>

			<Modal
				title={
					<>
						<Icon type='cloud-upload' style={{ marginRight: 8 }} />
						Fazendo upload das vinhetas
					</>
				}
				footer={null}
				visible={fallback?.uploading}
				closable={false}>
				{progress?.programIntro !== null && (
					<>
						<Typography.Text>Vinhetas de introdução de programa...</Typography.Text>
						<Progress percent={progress?.programIntro} showInfo />
						<Divider />
					</>
				)}

				{progress?.programOutro !== null && (
					<>
						<Typography.Text>Vinhetas de encerramento de programa...</Typography.Text>
						<Progress percent={progress?.programOutro} />
						<Divider />
					</>
				)}

				{progress?.blockIntro !== null && (
					<>
						<Typography.Text>Vinhetas de introdução de bloco...</Typography.Text>
						<Progress percent={progress?.blockIntro} />
						<Divider />
					</>
				)}

				{progress?.blockOutro !== null && (
					<>
						<Typography.Text>Vinhetas de encerramento de bloco...</Typography.Text>
						<Progress percent={progress?.blockOutro} />
						<Divider />
					</>
				)}

				{progress?.break !== null && (
					<>
						<Typography.Text>Vinhetas de passagem...</Typography.Text>
						<Progress percent={progress?.break} />
						<Divider />
					</>
				)}
			</Modal>
		</>
	);
};

export default connect(({ user }) => ({ user }))(CreateProgram);
